/**
 * Comment Component
 */

import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatComponentAlignmentFormat,
  formatGapFormat,
  formatLimitFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseGapFormat,
  parseLimitFormat,
  parseSpacingFormat,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  ComponentAlignment,
  GradientDirection,
  LimitInput,
  LimitSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
} from '../../types'
import commentTemplate from './comment.template.json'

export interface ICommentComponentSettings {
  // Data settings
  'comment-data-source': string
  'comment-data-comment': string
  'comment-data-author': string
  'comment-data-avatar': string
  'comment-data-parent': string
  'comment-data-user': string
  'comment-data-date': string
  'comment-data-likes': string
  'comment-data-dislikes': string
  'comment-data-action-menu': BuilderActions
  'comment-data-sort-type': string
  'comment-data-filter': any
  'comment-data-limit': LimitSettings

  // Design settings
  'comment-design-width': ResponsiveValue<string[] | number[]>
  'comment-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'comment-design-show-like-count': boolean
  'comment-design-show-dislike-count': boolean
  'comment-design-margin': ResponsiveValue<SpacingInputValue>
  'comment-design-item-gap': ResponsiveValue<{ value: number; unit: string }>
  'comment-design-typography': {
    'comment-design-author-typography': ResponsiveValue<TypographyValue>
    'comment-design-body-typography': ResponsiveValue<TypographyValue>
    'comment-design-reply-button-typography': ResponsiveValue<TypographyValue>
    'comment-design-date-typography': ResponsiveValue<TypographyValue>
  }
  'comment-design-group-text-color': ColorAdaptiveValue<string>
  'comment-design-comment-color': ColorAdaptiveValue<string>
  'comment-design-comment-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-comment-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-comment-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-comment-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'comment-design-scheme': any
  'comment-design-author-name-color': ColorAdaptiveValue<string>
  'comment-design-author-name-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-name-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-name-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-name-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'comment-design-author-avatar-color': ColorAdaptiveValue<string>
  'comment-design-author-avatar-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-avatar-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-avatar-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-author-avatar-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'comment-design-time-color': ColorAdaptiveValue<string>
  'comment-design-time-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-time-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-time-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-time-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'comment-design-like-color': ColorAdaptiveValue<string>
  'comment-design-like-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-like-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-like-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-like-color-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'comment-design-dislike-color': ColorAdaptiveValue<string>
  'comment-design-dislike-color-solid': ColorAdaptiveValue<ColorVariant>
  'comment-design-dislike-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-dislike-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'comment-design-dislike-color-gradient-direction': ColorAdaptiveValue<GradientDirection>

  // Actions
  'comment-action-comment-submit': BuilderActions
  'comment-action-reply-submit': BuilderActions
  'comment-action-author-click': BuilderActions
  'comment-action-like-click': BuilderActions
  'comment-action-dislike-click': BuilderActions

  // Behavior settings
  'comment-behavior-auto-approve': boolean
  'comment-behavior-show-like-count': boolean
  'comment-behavior-show-dislike-count': boolean
}

export interface ICommentComponent extends IBuilderComponent {
  componentId: 'comment'
  groupId: 'interactive'
  settings: ICommentComponentSettings
}

/**
 * Comment AI Input
 */
export interface AiCommentInput {
  dataSource: string // Table ID
  commentColumn?: string // Column ID for comment text
  authorColumn?: string // Column ID for author name
  avatarColumn?: string // Column ID for avatar image
  parentColumn?: string // Column ID for parent comment
  userColumn?: string // Column ID for current user
  dateColumn?: string // Column ID for date
  likesColumn?: string // Column ID for likes count
  dislikesColumn?: string // Column ID for dislikes count
  actionMenu?: string // Action menu ID
  sortType?: 'asc' | 'desc' // Sort direction
  filter?: string | any // Filter object (can be JSON string or object)
  limit?: LimitInput // Limit format (same as data-table)

  // Styling
  width?: string // Format: "w:100%"
  alignment?: string // Format: "parent" or "center"
  margin?: string // Format: "m:20px"
  itemGap?: string // Format: "gap:8px"
  showLikeCount?: boolean // Show like count
  showDislikeCount?: boolean // Show dislike count

  // Typography
  typography?: {
    author?: string // Author typography
    body?: string // Body typography
    replyButton?: string // Reply button typography
    date?: string // Date typography
  }

  // Colors (can use color format parser)
  commentColor?: string // Format: "solid:primary-40" or "adaptive"
  authorNameColor?: string // Format: "solid:primary-40" or "adaptive"
  authorAvatarColor?: string // Format: "solid:primary-40" or "adaptive"
  timeColor?: string // Format: "solid:primary-40" or "adaptive"
  likeColor?: string // Format: "solid:primary-40" or "adaptive"
  dislikeColor?: string // Format: "solid:primary-40" or "adaptive"

  // Behavior
  autoApprove?: boolean // Auto approve comments
  showLikeCountBehavior?: boolean // Show like count (behavior setting)
  showDislikeCountBehavior?: boolean // Show dislike count (behavior setting)

  // Actions
  actionCommentSubmit?: BuilderActions
  actionReplySubmit?: BuilderActions
  actionAuthorClick?: BuilderActions
  actionLikeClick?: BuilderActions
  actionDislikeClick?: BuilderActions
}

const COMMENT_DEFAULT_TEMPLATE = commentTemplate as unknown as ICommentComponentSettings

export class CommentComp {
  getTemplate(): ICommentComponentSettings {
    return COMMENT_DEFAULT_TEMPLATE
  }

  /**
   * format comment component settings to AI comment input
   */
  format(settings: ICommentComponentSettings): AiCommentInput {
    const result: AiCommentInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['comment-data-source']) {
      result.dataSource = settings['comment-data-source']
    }

    // Data - Columns
    if (settings['comment-data-comment']) {
      result.commentColumn = settings['comment-data-comment']
    }

    if (settings['comment-data-author']) {
      result.authorColumn = settings['comment-data-author']
    }

    if (settings['comment-data-avatar']) {
      result.avatarColumn = settings['comment-data-avatar']
    }

    if (settings['comment-data-parent']) {
      result.parentColumn = settings['comment-data-parent']
    }

    if (settings['comment-data-user']) {
      result.userColumn = settings['comment-data-user']
    }

    if (settings['comment-data-date']) {
      result.dateColumn = settings['comment-data-date']
    }

    if (settings['comment-data-likes']) {
      result.likesColumn = settings['comment-data-likes']
    }

    if (settings['comment-data-dislikes']) {
      result.dislikesColumn = settings['comment-data-dislikes']
    }

    // Data - Sort
    if (settings['comment-data-sort-type']) {
      result.sortType = settings['comment-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['comment-data-limit']) {
      result.limit = formatLimitFormat(settings['comment-data-limit'])
    }

    // Design - Width
    if (settings['comment-design-width']) {
      result.width = formatWidthFormat(settings['comment-design-width'])
    }

    // Design - Alignment
    if (settings['comment-design-comp-h-align']) {
      result.alignment =
        formatComponentAlignmentFormat(settings['comment-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Margin
    if (settings['comment-design-margin']) {
      result.margin = formatSpacingFormat(settings['comment-design-margin'])
    }

    // Design - Item Gap
    if (settings['comment-design-item-gap']) {
      result.itemGap = formatGapFormat(settings['comment-design-item-gap'])
    }

    // Design - Show Like/Dislike Count
    if (settings['comment-design-show-like-count'] !== undefined) {
      result.showLikeCount = settings['comment-design-show-like-count']
    }

    if (settings['comment-design-show-dislike-count'] !== undefined) {
      result.showDislikeCount = settings['comment-design-show-dislike-count']
    }

    // Design - Typography
    if (settings['comment-design-typography']) {
      result.typography = {}
      if (settings['comment-design-typography']['comment-design-author-typography']) {
        result.typography.author = formatTypographyFormat(
          settings['comment-design-typography']['comment-design-author-typography']
        )
      }
      if (settings['comment-design-typography']['comment-design-body-typography']) {
        result.typography.body = formatTypographyFormat(
          settings['comment-design-typography']['comment-design-body-typography']
        )
      }
      if (settings['comment-design-typography']['comment-design-reply-button-typography']) {
        result.typography.replyButton = formatTypographyFormat(
          settings['comment-design-typography']['comment-design-reply-button-typography']
        )
      }
      if (settings['comment-design-typography']['comment-design-date-typography']) {
        result.typography.date = formatTypographyFormat(
          settings['comment-design-typography']['comment-design-date-typography']
        )
      }
    }

    // Design - Colors
    if (settings['comment-design-comment-color']) {
      result.commentColor = formatColorFormat({
        colorType: settings['comment-design-comment-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-comment-color-solid'],
        colorStartGradient: settings['comment-design-comment-color-start-gradient'],
        colorEndGradient: settings['comment-design-comment-color-end-gradient'],
        colorGradientDirection: settings['comment-design-comment-color-gradient-direction'],
      })
    }

    if (settings['comment-design-author-name-color']) {
      result.authorNameColor = formatColorFormat({
        colorType: settings['comment-design-author-name-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-author-name-color-solid'],
        colorStartGradient: settings['comment-design-author-name-color-start-gradient'],
        colorEndGradient: settings['comment-design-author-name-color-end-gradient'],
        colorGradientDirection: settings['comment-design-author-name-color-gradient-direction'],
      })
    }

    if (settings['comment-design-author-avatar-color']) {
      result.authorAvatarColor = formatColorFormat({
        colorType: settings['comment-design-author-avatar-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-author-avatar-color-solid'],
        colorStartGradient: settings['comment-design-author-avatar-color-start-gradient'],
        colorEndGradient: settings['comment-design-author-avatar-color-end-gradient'],
        colorGradientDirection: settings['comment-design-author-avatar-color-gradient-direction'],
      })
    }

    if (settings['comment-design-time-color']) {
      result.timeColor = formatColorFormat({
        colorType: settings['comment-design-time-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-time-color-solid'],
        colorStartGradient: settings['comment-design-time-color-start-gradient'],
        colorEndGradient: settings['comment-design-time-color-end-gradient'],
        colorGradientDirection: settings['comment-design-time-color-gradient-direction'],
      })
    }

    if (settings['comment-design-like-color']) {
      result.likeColor = formatColorFormat({
        colorType: settings['comment-design-like-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-like-color-solid'],
        colorStartGradient: settings['comment-design-like-color-start-gradient'],
        colorEndGradient: settings['comment-design-like-color-end-gradient'],
        colorGradientDirection: settings['comment-design-like-color-gradient-direction'],
      })
    }

    if (settings['comment-design-dislike-color']) {
      result.dislikeColor = formatColorFormat({
        colorType: settings['comment-design-dislike-color'] as ColorAdaptiveValue<ColorType>,
        colorSolid: settings['comment-design-dislike-color-solid'],
        colorStartGradient: settings['comment-design-dislike-color-start-gradient'],
        colorEndGradient: settings['comment-design-dislike-color-end-gradient'],
        colorGradientDirection: settings['comment-design-dislike-color-gradient-direction'],
      })
    }

    // Behavior
    if (settings['comment-behavior-auto-approve'] !== undefined) {
      result.autoApprove = settings['comment-behavior-auto-approve']
    }

    if (settings['comment-behavior-show-like-count'] !== undefined) {
      result.showLikeCountBehavior = settings['comment-behavior-show-like-count']
    }

    if (settings['comment-behavior-show-dislike-count'] !== undefined) {
      result.showDislikeCountBehavior = settings['comment-behavior-show-dislike-count']
    }

    return result
  }

  /**
   * parse AI comment input to comment component settings
   */
  parse(input: AiCommentInput): ICommentComponentSettings {
    const result = deepMerge(COMMENT_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['comment-data-source'] = input.dataSource
    }

    // Data - Columns
    if (input.commentColumn) {
      result['comment-data-comment'] = input.commentColumn
    }

    if (input.authorColumn) {
      result['comment-data-author'] = input.authorColumn
    }

    if (input.avatarColumn) {
      result['comment-data-avatar'] = input.avatarColumn
    }

    if (input.parentColumn) {
      result['comment-data-parent'] = input.parentColumn
    }

    if (input.userColumn) {
      result['comment-data-user'] = input.userColumn
    }

    if (input.dateColumn) {
      result['comment-data-date'] = input.dateColumn
    }

    if (input.likesColumn) {
      result['comment-data-likes'] = input.likesColumn
    }

    if (input.dislikesColumn) {
      result['comment-data-dislikes'] = input.dislikesColumn
    }

    if (input.actionMenu) {
      result['comment-data-action-menu'] = input.actionMenu as any
    }

    // Data - Sort
    if (input.sortType) {
      result['comment-data-sort-type'] = input.sortType
    }

    // Data - Filter (use JSON.stringify for string input)
    if (input.filter !== undefined) {
      if (typeof input.filter === 'string') {
        try {
          result['comment-data-filter'] = JSON.parse(input.filter)
        } catch {
          // If parsing fails, use as-is
          result['comment-data-filter'] = input.filter
        }
      } else {
        result['comment-data-filter'] = input.filter
      }
    }

    // Data - Limit
    if (input.limit !== undefined) {
      const parsedLimit = parseLimitFormat(input.limit)
      if (parsedLimit) {
        result['comment-data-limit'] = parsedLimit
      }
    }

    // Design - Width
    if (input.width) {
      result['comment-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['comment-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['comment-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Item Gap
    if (input.itemGap) {
      result['comment-design-item-gap'] = parseGapFormat(input.itemGap)
    }

    // Design - Show Like/Dislike Count
    if (input.showLikeCount !== undefined) {
      result['comment-design-show-like-count'] = input.showLikeCount
    }

    if (input.showDislikeCount !== undefined) {
      result['comment-design-show-dislike-count'] = input.showDislikeCount
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.author) {
        const authorTypo = parseTypographyFormat(input.typography.author)
        result['comment-design-typography']['comment-design-author-typography'] = authorTypo
      }

      if (input.typography.body) {
        const bodyTypo = parseTypographyFormat(input.typography.body)
        result['comment-design-typography']['comment-design-body-typography'] = bodyTypo
      }

      if (input.typography.replyButton) {
        const replyTypo = parseTypographyFormat(input.typography.replyButton)
        result['comment-design-typography']['comment-design-reply-button-typography'] = replyTypo
      }

      if (input.typography.date) {
        const dateTypo = parseTypographyFormat(input.typography.date)
        result['comment-design-typography']['comment-design-date-typography'] = dateTypo
      }
    }

    // Design - Colors
    if (input.commentColor) {
      const colorParsed = parseColorFormat(input.commentColor)
      result['comment-design-comment-color'] = colorParsed.colorType
      result['comment-design-comment-color-solid'] = colorParsed.colorSolid
      result['comment-design-comment-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-comment-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-comment-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.authorNameColor) {
      const colorParsed = parseColorFormat(input.authorNameColor)
      result['comment-design-author-name-color'] = colorParsed.colorType
      result['comment-design-author-name-color-solid'] = colorParsed.colorSolid
      result['comment-design-author-name-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-author-name-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-author-name-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.authorAvatarColor) {
      const colorParsed = parseColorFormat(input.authorAvatarColor)
      result['comment-design-author-avatar-color'] = colorParsed.colorType
      result['comment-design-author-avatar-color-solid'] = colorParsed.colorSolid
      result['comment-design-author-avatar-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-author-avatar-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-author-avatar-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.timeColor) {
      const colorParsed = parseColorFormat(input.timeColor)
      result['comment-design-time-color'] = colorParsed.colorType
      result['comment-design-time-color-solid'] = colorParsed.colorSolid
      result['comment-design-time-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-time-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-time-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.likeColor) {
      const colorParsed = parseColorFormat(input.likeColor)
      result['comment-design-like-color'] = colorParsed.colorType
      result['comment-design-like-color-solid'] = colorParsed.colorSolid
      result['comment-design-like-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-like-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-like-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    if (input.dislikeColor) {
      const colorParsed = parseColorFormat(input.dislikeColor)
      result['comment-design-dislike-color'] = colorParsed.colorType
      result['comment-design-dislike-color-solid'] = colorParsed.colorSolid
      result['comment-design-dislike-color-start-gradient'] = colorParsed.colorStartGradient
      result['comment-design-dislike-color-end-gradient'] = colorParsed.colorEndGradient
      result['comment-design-dislike-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Behavior
    if (input.autoApprove !== undefined) {
      result['comment-behavior-auto-approve'] = input.autoApprove
    }

    if (input.showLikeCountBehavior !== undefined) {
      result['comment-behavior-show-like-count'] = input.showLikeCountBehavior
    }

    if (input.showDislikeCountBehavior !== undefined) {
      result['comment-behavior-show-dislike-count'] = input.showDislikeCountBehavior
    }

    // Actions
    if (input.actionCommentSubmit) {
      result['comment-action-comment-submit'] = input.actionCommentSubmit
    }

    if (input.actionReplySubmit) {
      result['comment-action-reply-submit'] = input.actionReplySubmit
    }

    if (input.actionAuthorClick) {
      result['comment-action-author-click'] = input.actionAuthorClick
    }

    if (input.actionLikeClick) {
      result['comment-action-like-click'] = input.actionLikeClick
    }

    if (input.actionDislikeClick) {
      result['comment-action-dislike-click'] = input.actionDislikeClick
    }

    return result
  }
}
